import { useState, useEffect, useRef } from 'react'
import ChartMenu, { MenuRow, MenuSwatch, MenuSelect, MenuDivider, MenuActions, MenuButton } from './menu/ChartMenu'

// ============================================
// CVDSettingsMenu — regular Cumulative Delta's toolbar (cdhist-20261025).
//
// Shared shell (menu/ChartMenu), AUTO-APPLY like DVPSettingsMenu: every
// control commits on the spot, persisted through the same scoped setting
// pattern (solcheddar:cvdConfig).
//
// STYLE: Candle = cumulative delta OHLC candles (the original look).
//        Histogram = each candle's own delta as a bar from zero.
// Colors are per style so switching never repaints the other one:
//   Candle    -> UP / DOWN body fill + WICK
//   Histogram -> + (positive delta) / − (negative delta)
// ============================================

export const CVD_DEFAULTS = {
  style:     'candle',
  upColor:   '#00ff08',   // = the original rgb(0, 255, 8)
  downColor: '#3f3f3f',   // = the original rgb(63, 63, 63)
  wickColor: '#ffffff',   // = the original white wick
  histPos:   '#00ff08',
  histNeg:   '#ef5350',
}

const STYLES = [
  { value: 'candle',    label: 'Candle' },
  { value: 'histogram', label: 'Histogram' },
]

export default function CVDSettingsMenu({ show, x, y, openLeft, config, onUpdate, onHide, onClose }) {
  const cfg = { ...CVD_DEFAULTS, ...(config || {}) }
  const [style, setStyle] = useState(cfg.style)
  const [upColor, setUpColor] = useState(cfg.upColor)
  const [downColor, setDownColor] = useState(cfg.downColor)
  const [wickColor, setWickColor] = useState(cfg.wickColor)
  const [histPos, setHistPos] = useState(cfg.histPos)
  const [histNeg, setHistNeg] = useState(cfg.histNeg)

  // Re-seed from config when the menu OPENS only (same as DVP).
  const wasShownRef = useRef(false)
  useEffect(() => {
    if (show && !wasShownRef.current) {
      const c = { ...CVD_DEFAULTS, ...(config || {}) }
      setStyle(c.style); setUpColor(c.upColor); setDownColor(c.downColor)
      setWickColor(c.wickColor); setHistPos(c.histPos); setHistNeg(c.histNeg)
    }
    wasShownRef.current = !!show
  }, [show, config])

  const commit = (patch) => onUpdate(patch)

  if (!show) return null

  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose}>
      <MenuRow>
        <MenuSelect value={style} options={STYLES}
          onChange={v => { setStyle(v); commit({ style: v }) }} />
        <MenuDivider />
        {style === 'histogram' ? (
          <>
            <MenuSwatch label="+" value={histPos} onChange={c => { setHistPos(c); commit({ histPos: c }) }} />
            <MenuDivider />
            <MenuSwatch label="−" value={histNeg} onChange={c => { setHistNeg(c); commit({ histNeg: c }) }} />
          </>
        ) : (
          <>
            <MenuSwatch label="UP" value={upColor} onChange={c => { setUpColor(c); commit({ upColor: c }) }} />
            <MenuDivider />
            <MenuSwatch label="DOWN" value={downColor} onChange={c => { setDownColor(c); commit({ downColor: c }) }} />
            <MenuDivider />
            <MenuSwatch label="WICK" value={wickColor} onChange={c => { setWickColor(c); commit({ wickColor: c }) }} />
          </>
        )}
      </MenuRow>
      <MenuActions>
        <MenuButton variant="danger" onClick={() => { onHide(); onClose() }}>HIDE</MenuButton>
      </MenuActions>
    </ChartMenu>
  )
}
